import xor from 'lodash/xor';
import * as ramda from 'ramda';

import {getSortFunc} from 'nutshell-core/utils/sorting/get-sort-func';

import type {TemplateGroup} from './template-list';
import type {SortObject} from './template-list-group';

/**
 * We need to do a little bit of jankery when sorting by last-sent time,
 * as our timestamps will sort a never-sent (null lastSentTime) template
 * as the most-recent.
 *
 * Expected behavior would be that a next-sent template would be AFTER
 * the oldest in DESC, and BEFORE the oldest in ASC. So we'll manually
 * yoink them out and prepend/append them accordingly after the sort.
 *
 * @param {TemplateGroup} templates - the group of templates
 * @param {SortObject} currentSort - describes the desired sort
 * @returns {TemplateGroup} - correctly sorted templates
 */
export function sortTemplates(templates: TemplateGroup, currentSort: SortObject): TemplateGroup {
    const {columnName, order} = currentSort;
    const isAlphabeticalSort = columnName === 'name';
    const sortFunc = getSortFunc(order === 'ascending', isAlphabeticalSort);
    const sort = ramda.sort(
        // @ts-expect-error
        sortFunc((template) =>
            isAlphabeticalSort ? template[columnName].toLowerCase() : template[columnName]
        )
    );
    const sortedTemplates = sort(templates);

    return currentSort.columnName === 'lastSentTime'
        ? // @ts-expect-error
          adjustForTimestamps(sortedTemplates, order)
        : // @ts-expect-error
          sortedTemplates;
}

function adjustForTimestamps(templates: TemplateGroup, order: string): TemplateGroup {
    const neverSent = templates.filter((template) => template.lastSentTime === null);
    const sent = xor(templates, neverSent);

    const adjustedSort = order === 'ascending' ? neverSent.concat(sent) : sent.concat(neverSent);

    return adjustedSort;
}
